import * as React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { useDispatch } from "react-redux";
import { UserDetailModal } from "utils/modals";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import { getUsersBySearch } from "modules/orderManagement/action";
import { USER_TYPE } from "utils/userType";
import { commonTableStyles } from "components/tables/styles";

interface Props {
  isEdit?: boolean;
  userType: number;
  disabled?: boolean;
  size?: "small" | "medium";
  disablePortal?: boolean;
  selectedUser?: UserDetailModal | null;
  onSelectUser: (user: UserDetailModal | null) => void;
  /** Optional label override */
  label?: string;
  /**
   * `filter` matches table filter panel fields (outlined small + shared styles).
   * Default leaves existing page UIs unchanged.
   */
  appearance?: "default" | "filter";
  fieldSx?: SxProps<Theme>;
}

const SEARCH_DEBOUNCE_MS = 400;

export default function UserDopdown({
  userType,
  onSelectUser,
  disabled = false,
  size = "medium",
  disablePortal = false,
  selectedUser = null,
  label,
  appearance = "default",
  fieldSx,
}: Props) {
  const dispatch: any = useDispatch();
  const isFilter = appearance === "filter";

  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [inputValue, setInputValue] = React.useState("");
  const [selected, setSelected] = React.useState<UserDetailModal | null>(
    selectedUser
  );
  const [loading, setLoading] = React.useState(false);
  const [options, setOptions] = React.useState<UserDetailModal[]>([]);
  const requestIdRef = React.useRef(0);

  const dropdownLabel =
    label ||
    (userType === USER_TYPE.CUSTOMER ? "Select User" : "Select Franchise");

  const fieldSize = isFilter ? "small" : size;

  const getUserOptionLabel = React.useCallback((option: any) => {
    if (!option) return "";
    const name =
      option?.fullName ||
      `${option?.firstName || ""} ${option?.lastName || ""}`.trim();
    return option?.phoneNumber ? `${name} (+91-${option.phoneNumber})` : name;
  }, []);

  React.useEffect(() => {
    const nextId = selectedUser?.id || "";
    const currentId = selected?.id || "";
    if (nextId === currentId && !!selectedUser === !!selected) {
      if (selectedUser) {
        setInputValue(getUserOptionLabel(selectedUser));
      }
      return;
    }
    setSelected(selectedUser);
    if (selectedUser) {
      setInputValue(getUserOptionLabel(selectedUser));
    } else {
      setInputValue("");
      setSearch("");
      setDebouncedSearch("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser?.id]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search]);

  const fetchUsersData = React.useCallback(
    (query: string) => {
      const requestId = ++requestIdRef.current;
      setLoading(true);
      dispatch(
        getUsersBySearch(query, userType, (result: UserDetailModal[]) => {
          if (requestId !== requestIdRef.current) return;
          setOptions(Array.isArray(result) ? result : []);
          setLoading(false);
        })
      );
    },
    [dispatch, userType]
  );

  React.useEffect(() => {
    if (!open) return;
    fetchUsersData(debouncedSearch);
  }, [open, debouncedSearch, fetchUsersData]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setOptions((prev) =>
      selected && !prev.some((option) => option.id === selected.id)
        ? [selected, ...prev]
        : prev
    );
  };

  const handleChange = (
    _: React.SyntheticEvent,
    value: UserDetailModal | null
  ) => {
    setSelected(value);
    onSelectUser(value);
    if (value) {
      setInputValue(getUserOptionLabel(value));
      setSearch("");
      setDebouncedSearch("");
    }
  };

  const handleInputChange = (
    _: React.SyntheticEvent,
    newInputValue: string,
    reason: string
  ) => {
    if (reason === "input") {
      setInputValue(newInputValue);
      setSearch(newInputValue);
      return;
    }
    if (reason === "clear") {
      setInputValue("");
      setSearch("");
      setDebouncedSearch("");
      setSelected(null);
      onSelectUser(null);
      return;
    }
    if (newInputValue) {
      setInputValue(newInputValue);
    }
  };

  const dropdownOptions =
    selected && !options.some((option) => option.id === selected.id)
      ? [selected, ...options]
      : options;

  const autocomplete = (
    <Autocomplete
      fullWidth
      size={fieldSize}
      disablePortal={disablePortal}
      open={open}
      disabled={disabled}
      onOpen={handleOpen}
      onClose={handleClose}
      value={selected}
      isOptionEqualToValue={(option, value) =>
        Boolean(option?.id) && option.id === value?.id
      }
      getOptionLabel={getUserOptionLabel}
      options={dropdownOptions}
      loading={loading}
      onChange={handleChange}
      filterOptions={(x) => x}
      sx={fieldSx ? undefined : isFilter ? commonTableStyles.filterFieldRoot : undefined}
      renderOption={(props, option, _, ownerState) => {
        const { key, ...optionProps } = props;
        return (
          <Box key={key} component="li" {...optionProps}>
            {ownerState.getOptionLabel(option)}
          </Box>
        );
      }}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      renderInput={(params) => (
        <TextField
          {...params}
          size={fieldSize}
          variant="outlined"
          label={fieldSx ? undefined : dropdownLabel}
          placeholder={fieldSx ? "Search" : undefined}
        />
      )}
    />
  );

  if (!fieldSx) return autocomplete;

  return (
    <Box sx={fieldSx}>
      <Box
        component="span"
        sx={{
          display: "block",
          mb: "6px",
          fontSize: 12,
          fontWeight: 600,
          lineHeight: "16px",
          color: "#5C5870",
        }}
      >
        {dropdownLabel}
      </Box>
      {autocomplete}
    </Box>
  );
}
